// src/components/BlogCategoryFilter.tsx
import { jsx, jsxs } from "react/jsx-runtime";
function BlogCategoryFilter({
  tags,
  activeTag,
  onTagChange,
  allLabel = "All"
}) {
  const baseClasses = "px-4 py-2 text-sm font-medium rounded-full transition-all duration-200";
  const activeClasses = "bg-slate-900 text-white shadow-soft";
  const inactiveClasses = "bg-slate-100 text-slate-600 hover:bg-slate-200";
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-2", role: "group", "aria-label": "Filter by category", children: [
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        "data-active": activeTag === null ? "true" : "false",
        className: `${baseClasses} ${activeTag === null ? activeClasses : inactiveClasses}`,
        onClick: () => onTagChange(null),
        children: allLabel
      }
    ),
    tags.map((tag) => {
      const isActive = activeTag === tag.slug;
      return /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          "data-active": isActive ? "true" : "false",
          className: `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`,
          onClick: () => onTagChange(tag.slug),
          children: tag.name
        },
        tag.id
      );
    })
  ] });
}

// src/components/BlogCard.tsx
import { Fragment, jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}
function BlogCard({
  post,
  basePath = "/blog",
  className = "",
  showPrimaryTag = true
}) {
  const postUrl = `${basePath}/${post.slug}`;
  return /* @__PURE__ */ jsxs2(
    "article",
    {
      className: `group bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-soft-lg hover:-translate-y-1 transition-all duration-300 ${className}`,
      children: [
        post.feature_image && /* @__PURE__ */ jsx2("a", { href: postUrl, className: "block", children: /* @__PURE__ */ jsx2("img", { src: post.feature_image, alt: post.title, className: "w-full h-48 object-cover" }) }),
        /* @__PURE__ */ jsxs2("div", { className: "p-5", children: [
          post.primary_tag && showPrimaryTag && /* @__PURE__ */ jsx2("span", { className: "inline-block px-2.5 py-1 text-xs font-semibold uppercase tracking-wide rounded-full bg-sky-50 text-sky-600 mb-3", children: post.primary_tag.name }),
          /* @__PURE__ */ jsx2("h3", { className: "text-lg font-semibold mb-2 leading-snug", children: /* @__PURE__ */ jsx2("a", { href: postUrl, className: "text-slate-900 group-hover:text-slate-600 transition-colors", children: post.title }) }),
          /* @__PURE__ */ jsx2("p", { className: "text-slate-500 text-sm mb-4 line-clamp-3 leading-relaxed", children: post.excerpt }),
          /* @__PURE__ */ jsxs2("div", { className: "flex items-center text-xs text-slate-400 gap-2 pt-3 border-t border-slate-100", children: [
            /* @__PURE__ */ jsx2("time", { dateTime: post.published_at, children: formatDate(post.published_at) }),
            /* @__PURE__ */ jsx2("span", { "aria-hidden": "true", children: "\xB7" }),
            /* @__PURE__ */ jsxs2("span", { children: [
              post.reading_time,
              " min read"
            ] }),
            post.authors.length > 0 && /* @__PURE__ */ jsxs2(Fragment, { children: [
              /* @__PURE__ */ jsx2("span", { "aria-hidden": "true", children: "\xB7" }),
              /* @__PURE__ */ jsx2("span", { children: post.authors[0].name })
            ] })
          ] })
        ] })
      ]
    }
  );
}

// src/components/BlogList.tsx
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
function BlogList({
  posts,
  basePath,
  emptyMessage = "No posts available.",
  className = "",
  showPrimaryTag = true
}) {
  if (posts.length === 0) {
    return /* @__PURE__ */ jsx3("div", { className: `py-16 text-center ${className}`, children: /* @__PURE__ */ jsxs3("div", { className: "mx-auto max-w-md rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10", children: [
      /* @__PURE__ */ jsx3("div", { className: "mb-4 text-5xl opacity-40", children: "\u{1F4DD}" }),
      /* @__PURE__ */ jsx3("p", { className: "text-lg font-medium text-slate-600", children: emptyMessage })
    ] }) });
  }
  return /* @__PURE__ */ jsx3("div", { className: `grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ${className}`, children: posts.map((post) => /* @__PURE__ */ jsx3(BlogCard, { post, basePath, showPrimaryTag }, post.slug)) });
}

// src/pages/hooks.ts
import { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

// src/seo/article-schema.ts
function generateArticleJsonLd(post, baseUrl, lang = "de") {
  const url = `${baseUrl}/${lang}/blog/${post.slug}`;
  const description = post.meta_description || post.excerpt;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    url,
    datePublished: post.published_at,
    dateModified: post.updated_at,
    description,
    inLanguage: lang === "en" ? "en-US" : "de-DE",
    author: {
      "@type": "Person",
      name: post.authors[0]?.name ?? "Unknown"
    },
    publisher: {
      "@type": "Organization",
      name: new URL(baseUrl).hostname
    }
  };
  if (post.feature_image) {
    jsonLd.image = post.feature_image;
  }
  return jsonLd;
}

// src/seo/faq-schema.ts
function stripHtmlTags(html) {
  return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}
function extractFaqFromHtml(html) {
  if (!html) return [];
  const items = [];
  const h2Regex = /<h2[^>]*>(.*?)<\/h2>/gi;
  const matches = [];
  let match;
  while ((match = h2Regex.exec(html)) !== null) {
    matches.push({
      text: stripHtmlTags(match[1]),
      index: match.index,
      fullMatchLength: match[0].length
    });
  }
  for (let i = 0; i < matches.length; i++) {
    const heading = matches[i];
    const question = heading.text.trim();
    if (!question.endsWith("?")) continue;
    const answerStart = heading.index + heading.fullMatchLength;
    const answerEnd = i + 1 < matches.length ? matches[i + 1].index : html.length;
    const answerHtml = html.slice(answerStart, answerEnd);
    const answer = stripHtmlTags(answerHtml);
    if (answer) {
      items.push({ question, answer });
    }
  }
  return items;
}
function generateFaqJsonLd(items) {
  if (items.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer
      }
    }))
  };
}

// src/utils/filter-tags.ts
function filterPublicTags(tags) {
  return tags.filter((t) => !t.name.startsWith("#") && !t.slug.startsWith("hash-"));
}

// src/pages/hooks.ts
var SUPPORTED_BLOG_LANGS = ["en", "de", "it", "es", "fr"];
function detectLang(pathname) {
  for (const lang of SUPPORTED_BLOG_LANGS) {
    if (pathname === `/${lang}` || pathname.startsWith(`/${lang}/`)) return lang;
  }
  return "en";
}
function computeBasePath(lang, langPrefix) {
  if (langPrefix === "all") return `/${lang}/blog`;
  if (lang === "en") return "/en/blog";
  if (langPrefix === "both") return `/${lang}/blog`;
  return "/blog";
}
function extractUniquePublicTags(posts) {
  const allTags = posts.flatMap((p) => p.tags);
  const publicTags = filterPublicTags(allTags);
  const seen = /* @__PURE__ */ new Set();
  const result = [];
  for (const tag of publicTags) {
    if (!seen.has(tag.slug)) {
      seen.add(tag.slug);
      result.push(tag);
    }
  }
  return result;
}
function useBlogList(config) {
  const { pathname } = useLocation();
  const lang = detectLang(pathname);
  const isEnglish = lang === "en";
  const basePath = computeBasePath(lang, config.langPrefix);
  const fallbackLang = config.fallbackLang ?? "en";
  const [posts, setPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeTag, setActiveTag] = useState(null);
  useEffect(() => {
    let cancelled = false;
    async function fetchForLang(fetchLang) {
      const params = new URLSearchParams();
      params.set("lang", fetchLang);
      if (activeTag) params.set("tag", activeTag);
      const res = await fetch(`/api/blog?${params.toString()}`);
      if (!res.ok) return [];
      const data = await res.json();
      return data.posts ?? [];
    }
    async function fetchPosts() {
      setLoading(true);
      setError(false);
      try {
        let fetchedPosts = await fetchForLang(lang);
        if (fetchedPosts.length === 0 && lang !== fallbackLang) {
          fetchedPosts = await fetchForLang(fallbackLang);
        }
        if (!cancelled) {
          setPosts(fetchedPosts);
          setTags(extractUniquePublicTags(fetchedPosts));
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setPosts([]);
          setTags([]);
          setError(true);
          setLoading(false);
        }
      }
    }
    fetchPosts();
    return () => {
      cancelled = true;
    };
  }, [lang, activeTag, fallbackLang]);
  return {
    posts,
    tags,
    loading,
    error,
    activeTag,
    setActiveTag,
    lang,
    basePath,
    isEnglish
  };
}
function useBlogPost(config) {
  const { pathname } = useLocation();
  const { slug } = useParams();
  const lang = detectLang(pathname);
  const isEnglish = lang === "en";
  const basePath = computeBasePath(lang, config.langPrefix);
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const scriptRefs = useRef([]);
  useEffect(() => {
    if (!slug) {
      setPost(null);
      setLoading(false);
      setNotFound(true);
      return;
    }
    let cancelled = false;
    async function fetchPost() {
      setLoading(true);
      setError(false);
      setNotFound(false);
      setPost(null);
      try {
        const params = new URLSearchParams();
        params.set("lang", lang);
        const res = await fetch(`/api/blog/${slug}?${params.toString()}`);
        if (!res.ok) {
          if (!cancelled) {
            setPost(null);
            if (res.status === 404 || res.status === 401) {
              setNotFound(true);
            } else {
              setError(true);
            }
            setLoading(false);
          }
          return;
        }
        const data = await res.json();
        if (!cancelled) {
          if (data.post) {
            setPost(data.post);
          } else {
            setPost(null);
            setNotFound(true);
          }
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setPost(null);
          setError(true);
          setLoading(false);
        }
      }
    }
    fetchPost();
    return () => {
      cancelled = true;
    };
  }, [slug, lang]);
  useEffect(() => {
    cleanupScripts();
    if (!post) return;
    const scripts = [];
    const articleData = generateArticleJsonLd(
      post,
      config.baseUrl,
      lang
    );
    const articleScript = createJsonLdScript(articleData);
    document.head.appendChild(articleScript);
    scripts.push(articleScript);
    const faqItems = extractFaqFromHtml(post.html);
    const faqData = generateFaqJsonLd(faqItems);
    if (faqData) {
      const faqScript = createJsonLdScript(faqData);
      document.head.appendChild(faqScript);
      scripts.push(faqScript);
    }
    scriptRefs.current = scripts;
    return cleanupScripts;
  }, [post, config.baseUrl, lang, cleanupScripts]);
  function cleanupScripts() {
    for (const script of scriptRefs.current) {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    }
    scriptRefs.current = [];
  }
  return {
    post,
    loading,
    error,
    notFound,
    lang,
    basePath,
    isEnglish
  };
}
function createJsonLdScript(data) {
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.textContent = JSON.stringify(data);
  return script;
}

// src/pages/BlogListPage.tsx
import { Fragment as Fragment2, jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
function BlogListPage({ config }) {
  const { posts, tags, loading, error, activeTag, setActiveTag, isEnglish, basePath } = useBlogList(config);
  const heroTitle = config.heroTitle ? isEnglish ? config.heroTitle.en : config.heroTitle.de : "Blog";
  const heroSubtitle = config.heroSubtitle ? isEnglish ? config.heroSubtitle.en : config.heroSubtitle.de : void 0;
  return /* @__PURE__ */ jsxs4("div", { "data-testid": "blog-list-page", children: [
    /* @__PURE__ */ jsx4("section", { className: "pt-16 md:pt-24 pb-10 md:pb-14 bg-white", children: /* @__PURE__ */ jsx4("div", { className: "mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl", children: /* @__PURE__ */ jsxs4("div", { className: "text-center max-w-3xl mx-auto", children: [
      /* @__PURE__ */ jsx4("h1", { className: "text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 mt-3", children: heroTitle }),
      heroSubtitle && /* @__PURE__ */ jsx4("p", { className: "mt-4 text-lg md:text-xl text-slate-500 max-w-2xl mx-auto", children: heroSubtitle })
    ] }) }) }),
    /* @__PURE__ */ jsx4("section", { className: "pb-16 md:pb-24", children: /* @__PURE__ */ jsxs4("div", { className: "mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl", children: [
      loading && /* @__PURE__ */ jsx4("div", { "data-testid": "blog-loading", className: "py-16 text-center", children: /* @__PURE__ */ jsxs4("div", { className: "mx-auto max-w-sm", children: [
        /* @__PURE__ */ jsxs4("div", { className: "mb-6 flex justify-center gap-2", children: [
          /* @__PURE__ */ jsx4("div", { className: "h-3 w-3 animate-bounce rounded-full bg-teal-400 [animation-delay:0ms]" }),
          /* @__PURE__ */ jsx4("div", { className: "h-3 w-3 animate-bounce rounded-full bg-teal-400 [animation-delay:150ms]" }),
          /* @__PURE__ */ jsx4("div", { className: "h-3 w-3 animate-bounce rounded-full bg-teal-400 [animation-delay:300ms]" })
        ] }),
        /* @__PURE__ */ jsx4("p", { className: "text-slate-400", children: isEnglish ? "Loading posts..." : "Lade Beitr\xE4ge..." })
      ] }) }),
      error && /* @__PURE__ */ jsx4("div", { "data-testid": "blog-error", className: "py-16 text-center", children: /* @__PURE__ */ jsxs4("div", { className: "mx-auto max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm", children: [
        /* @__PURE__ */ jsx4("div", { className: "mb-4 text-4xl", children: "\u26A0\uFE0F" }),
        /* @__PURE__ */ jsx4("h2", { className: "mb-2 text-lg font-semibold text-slate-800", children: isEnglish ? "Could not load posts" : "Beitr\xE4ge konnten nicht geladen werden" }),
        /* @__PURE__ */ jsx4("p", { className: "mb-6 text-sm text-slate-500", children: isEnglish ? "Something went wrong. Please try again in a moment." : "Etwas ist schiefgelaufen. Bitte versuche es gleich erneut." }),
        /* @__PURE__ */ jsx4(
          "button",
          {
            onClick: () => window.location.reload(),
            className: "rounded-full bg-teal-500 px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-teal-600",
            children: isEnglish ? "Retry" : "Erneut versuchen"
          }
        )
      ] }) }),
      !loading && !error && /* @__PURE__ */ jsxs4(Fragment2, { children: [
        tags.length > 0 && /* @__PURE__ */ jsx4("div", { className: "mb-8", children: /* @__PURE__ */ jsx4(
          BlogCategoryFilter,
          {
            tags,
            activeTag,
            onTagChange: setActiveTag,
            allLabel: isEnglish ? "All" : "Alle"
          }
        ) }),
        /* @__PURE__ */ jsx4(
          BlogList,
          {
            posts,
            basePath,
            emptyMessage: isEnglish ? "No posts available yet." : "Noch keine Beitr\xE4ge verf\xFCgbar.",
            showPrimaryTag: tags.length === 0
          }
        )
      ] })
    ] }) })
  ] });
}

// src/components/BlogPostContent.tsx
import { Fragment as Fragment3, jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
function formatDate2(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}
function capitalize(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
function BlogPostContent({
  post,
  basePath = "/blog",
  proseClassName = "prose prose-lg max-w-none"
}) {
  const breadcrumbLabel = capitalize(basePath.replace(/^\//, "").replace(/^(en|de)\//, ""));
  return /* @__PURE__ */ jsxs5("article", { className: "max-w-3xl mx-auto", children: [
    /* @__PURE__ */ jsxs5("nav", { className: "text-sm text-slate-400 mb-8", "aria-label": "Breadcrumb", children: [
      /* @__PURE__ */ jsx5("a", { href: basePath, className: "hover:text-slate-600 transition-colors", children: breadcrumbLabel }),
      /* @__PURE__ */ jsx5("span", { className: "mx-2", "aria-hidden": "true", children: "/" }),
      /* @__PURE__ */ jsx5("span", { className: "text-slate-600", "aria-hidden": "true", children: "\u2026" })
    ] }),
    post.primary_tag && /* @__PURE__ */ jsx5("span", { className: "inline-block px-3 py-1 text-xs font-semibold uppercase tracking-wide rounded-full bg-sky-50 text-sky-600 mb-5", children: post.primary_tag.name }),
    /* @__PURE__ */ jsx5("h1", { className: "text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-6 leading-tight", children: post.title }),
    /* @__PURE__ */ jsxs5("div", { className: "flex items-center text-sm text-slate-400 mb-10 gap-3", children: [
      post.authors.length > 0 && /* @__PURE__ */ jsxs5(Fragment3, { children: [
        /* @__PURE__ */ jsx5("span", { className: "font-medium text-slate-600", children: post.authors[0].name }),
        /* @__PURE__ */ jsx5("span", { "aria-hidden": "true", children: "\xB7" })
      ] }),
      /* @__PURE__ */ jsx5("time", { dateTime: post.published_at, children: formatDate2(post.published_at) }),
      /* @__PURE__ */ jsx5("span", { "aria-hidden": "true", children: "\xB7" }),
      /* @__PURE__ */ jsxs5("span", { children: [
        post.reading_time,
        " min read"
      ] })
    ] }),
    post.feature_image && /* @__PURE__ */ jsx5("img", { src: post.feature_image, alt: post.title, className: "w-full rounded-2xl mb-10" }),
    /* @__PURE__ */ jsx5(
      "div",
      {
        "data-testid": "post-content",
        className: proseClassName,
        dangerouslySetInnerHTML: { __html: post.html }
      }
    ),
    post.tags.filter((t) => !t.name.startsWith("#") && !t.slug.startsWith("hash-")).length > 0 && /* @__PURE__ */ jsx5("div", { className: "mt-12 pt-8 border-t border-slate-200", children: /* @__PURE__ */ jsx5("div", { className: "flex flex-wrap gap-2", children: post.tags.filter((tag) => !tag.name.startsWith("#") && !tag.slug.startsWith("hash-")).map((tag) => /* @__PURE__ */ jsx5(
      "span",
      {
        className: "inline-block px-3 py-1.5 text-xs font-medium rounded-full bg-slate-100 text-slate-600 transition-colors hover:bg-slate-200",
        children: tag.name
      },
      tag.id
    )) }) })
  ] });
}

// src/pages/BlogPostPage.tsx
import { jsx as jsx6, jsxs as jsxs6 } from "react/jsx-runtime";
function BlogPostPage({ config }) {
  const { post, loading, error, notFound, isEnglish, basePath } = useBlogPost(config);
  if (loading) {
    return /* @__PURE__ */ jsx6("div", { "data-testid": "blog-loading", className: "py-24 text-center", children: /* @__PURE__ */ jsxs6("div", { className: "mx-auto max-w-sm", children: [
      /* @__PURE__ */ jsxs6("div", { className: "mb-6 flex justify-center gap-2", children: [
        /* @__PURE__ */ jsx6("div", { className: "h-3 w-3 animate-bounce rounded-full bg-teal-400 [animation-delay:0ms]" }),
        /* @__PURE__ */ jsx6("div", { className: "h-3 w-3 animate-bounce rounded-full bg-teal-400 [animation-delay:150ms]" }),
        /* @__PURE__ */ jsx6("div", { className: "h-3 w-3 animate-bounce rounded-full bg-teal-400 [animation-delay:300ms]" })
      ] }),
      /* @__PURE__ */ jsx6("p", { className: "text-slate-400", children: isEnglish ? "Loading post..." : "Lade Beitrag..." })
    ] }) });
  }
  if (error) {
    return /* @__PURE__ */ jsx6("div", { "data-testid": "blog-error", className: "py-24 text-center", children: /* @__PURE__ */ jsxs6("div", { className: "mx-auto max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm", children: [
      /* @__PURE__ */ jsx6("div", { className: "mb-4 text-4xl", children: "\u26A0\uFE0F" }),
      /* @__PURE__ */ jsx6("h2", { className: "mb-2 text-lg font-semibold text-slate-800", children: isEnglish ? "Could not load post" : "Beitrag konnte nicht geladen werden" }),
      /* @__PURE__ */ jsx6("p", { className: "mb-6 text-sm text-slate-500", children: isEnglish ? "Something went wrong. Please try again in a moment." : "Etwas ist schiefgelaufen. Bitte versuche es gleich erneut." }),
      /* @__PURE__ */ jsx6(
        "button",
        {
          onClick: () => window.location.reload(),
          className: "rounded-full bg-teal-500 px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-teal-600",
          children: isEnglish ? "Retry" : "Erneut versuchen"
        }
      )
    ] }) });
  }
  if (notFound || !post) {
    return /* @__PURE__ */ jsx6("div", { "data-testid": "blog-not-found", className: "py-24 text-center", children: /* @__PURE__ */ jsxs6("div", { className: "mx-auto max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm", children: [
      /* @__PURE__ */ jsx6("div", { className: "mb-4 text-5xl opacity-40", children: "\u{1F50E}" }),
      /* @__PURE__ */ jsx6("h2", { className: "mb-2 text-lg font-semibold text-slate-800", children: isEnglish ? "Post not found" : "Artikel nicht gefunden" }),
      /* @__PURE__ */ jsx6("p", { className: "mb-6 text-sm text-slate-500", children: isEnglish ? "This post may have been moved or deleted." : "Dieser Beitrag wurde m\xF6glicherweise verschoben oder gel\xF6scht." }),
      /* @__PURE__ */ jsx6(
        "a",
        {
          href: basePath,
          className: "inline-block rounded-full bg-teal-500 px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-teal-600",
          children: isEnglish ? "Back to Blog" : "Zur\xFCck zum Blog"
        }
      )
    ] }) });
  }
  return /* @__PURE__ */ jsx6("div", { "data-testid": "blog-post-page", children: /* @__PURE__ */ jsx6("section", { className: "py-16 md:py-24 bg-white", children: /* @__PURE__ */ jsx6("div", { className: "mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl", children: /* @__PURE__ */ jsx6(
    BlogPostContent,
    {
      post,
      basePath,
      proseClassName: config.proseClassName ?? "prose prose-lg max-w-none"
    }
  ) }) }) });
}
export {
  BlogListPage,
  BlogPostPage,
  useBlogList,
  useBlogPost
};
//# sourceMappingURL=pages.mjs.map